<template>
  <v-main class="pa-0">
    <template v-if="auth === true">
      <template v-if="!headerLogoHidden || !stepperHidden">
        <Header
          :stepPoint="stepPoint"
          :agencyName="agencyNameValue"
          :agencyTel="agencyPhoneNumberValue"
          :stepper="stepper"
          :applicationFormPage="applicationFormPage"
          :applicationFormTotalPage="applicationFormTotalPage"
          :stepperHidden="stepperHidden"
          :headerTitleHidden="headerTitleHidden"
          :telIconHidden="telIconHidden"
          :stepperText="stepperText"
        />
      </template>
      <router-view :stepPoint="stepPoint" :style="computedStyle" />
    </template>
  </v-main>
</template>

<script>
import Header from '@/components/organisms/contractor/Header';
import { mapGetters, mapActions } from 'vuex';
import {
  nonHeaderLogoPathName,
  nonHeaderPathName,
  nonHeaderTitlePathName,
  stepList,
  completedStepNumber,
} from '@/lib/const';
import { Status } from '@/lib/status';
import { tokenCheck } from '@/apis/contractor/certifications';
import { getContract } from '@/apis/contractor/contracts';
import { getValidStartDate } from '@/lib/commonUtil';

export default {
  name: 'Main',
  components: {
    Header,
  },
  data() {
    return {
      headerLogoHidden: true,
      stepperHidden: true,
      headerTitleHidden: true,
      telIconHidden: true,
      stepPoint: null,
      agencyNameValue: '',
      agencyPhoneNumberValue: '',
      stepper: [],
      applicationFormPage: null,
      applicationFormTotalPage: 4, // TODO: 改修後のお申込み内容確認の総ページ数を要確認
      auth: false,
      stepperText: '',
    };
  },
  computed: {
    computedStyle() {
      return {
        paddingTop: `${this.headerLogoHidden ? 0 : (this.stepperHidden ? 140 : 188)}px`
      };
    }
  },
  methods: {
    ...mapGetters('contractor', ['urlToken', 'accessToken', 'refreshToken']),
    ...mapActions('contractor', ['setUrlToken', 'setAccessToken']),
    ...mapGetters('contract', [
      'needUpload',
      'agencyName',
      'agencyPhoneNumber',
      'pendingStep',
      'pendingApplicationFormsPage',
    ]),
    ...mapActions('contract', ['setIsProcedureSkipped']),

    toggleHeaderDisplay(val) {
      const headerList = {
        headerLogoHidden: nonHeaderLogoPathName,
        headerTitleHidden: nonHeaderTitlePathName,
        stepperHidden: nonHeaderPathName,
      };

      Object.keys(headerList).forEach(function(key, index) {
        this[key] = headerList[key].contractor.indexOf(val.name) >= 0;
      }, this);

      // ご契約成立のご案内画面のみ電話番号アイコンを非表示にする
      this.telIconHidden = val.name === 'ProcedureComplete';
    },

    setHeaderTitle(val) {
      this.stepper = this.needUpload() ? stepList.upload : stepList.nonUpload;
      const step = this.stepper.find(
        step =>
          step.stepName === val.name && (!step.hash || step.hash === val.hash)
      );
      if (step) {
        this.stepPoint = step.step;
        this.stepperText = step.text;
      }
    },
  },
  watch: {
    async $route(val) {
      this.auth = false;
      // エラー画面及びメンテナンス画面では確認しない
      if (val.name !== 'Error' && val.name !== 'Maintenance') {
        // URLトークンが存在しない場合エラー画面に遷移
        if (!this.urlToken()) {
          if (val.query.token) {
            this.setUrlToken(val.query.token);
          } else {
            return this.$router.replace('/Error');
          }
        }

        // アクセストークンチェック
        if (val.meta.requiredAuth) {
          if (this.accessToken()) {
            // アクセストークンの確認
            const accessToken = await tokenCheck().catch(() => false);
            if (accessToken) {
              // アクセストークンの更新
              this.setAccessToken(accessToken);
            } else {
              // アクセストークンが不正な場合全ての情報を破棄してエラー画面に遷移
              localStorage.clear();
              this.stepperHidden = true;
              return this.$router.replace('/Error');
            }
          } else {
            return this.$router.replace('/');
          }

          // 閲覧エラー画面の場合
          if (val.name === 'ReadError') {
            this.auth = true;
            // 以降の処理を中止する
            return;
          }

          // 案件情報の取得
          const contract = await getContract().catch(() => {});
          const startDate = getValidStartDate(contract.startDate, contract.calculationBaseDate);
          // 案件閲覧ステータスの取得
          const contractReadStatus = Status.getContractRead(
            contract.status,
            startDate
          );

          // 案件が閲覧可でない場合
          if (!Status.isAvailable(contractReadStatus)) {
            // 閲覧エラー画面に遷移
            return this.$router.replace({ name: 'ReadError' });
          }

          // 案件ステータスが続行可でない場合
          if (
            val.name !== 'ProcedureComplete' &&
            !Status.isContinuable(contract.status)
          )
            // 最終画面に遷移
            return this.$router.replace({ name: 'ProcedureComplete' });
        }

        if (val.name == 'Login' && this.refreshToken()) {
          // トークンの確認
          const accessToken = await tokenCheck().catch(() => false);
          if (accessToken) {
            // アクセストークンの更新
            this.setAccessToken(accessToken);
            this.setIsProcedureSkipped(true);
            return this.$router.replace({ name: 'Tos' });
          }
        }

        if (
          val.name !== 'ProcedureComplete' &&
          this.pendingStep() === completedStepNumber &&
          val.meta.requiredAuth
        )
          // 確認ステータスが完了状態の場合は最終画面に遷移させる
          return this.$router.replace({ name: 'ProcedureComplete' });

        // 画面に応じて、表示するヘッダー要素を変更する
        this.toggleHeaderDisplay(val);

        // ヘッダータイトルの設定
        if (!this.headerTitleHidden) {
          this.setHeaderTitle(val);
        }

        if (!this.stepperHidden) {
          const step = this.stepper.find(
            step =>
              step.stepName === val.name &&
              (!step.hash || step.hash === val.hash)
          );
          if (step) {
            // 遷移先を確認済みのページまでに限定
            if (!this.pendingStep())
              return this.$router.replace({ name: 'Tos' });
            if (this.pendingStep() < step.step) {
              const pendingStep = this.stepper.find(
                step => step.step === this.pendingStep()
              );
              return this.$router.replace({ name: pendingStep.stepName });
            }

            if (step.stepName === 'ReviewContract') {
              const pageNumber = isNaN(val.params.pageNumber)
                ? 0
                : parseInt(val.params.pageNumber);
              if (
                pageNumber < 1 ||
                this.pendingApplicationFormsPage() < pageNumber
              ) {
                // 遷移先を確認済みの申込内容までに限定
                return this.$router.replace({
                  name: step.stepName,
                  params: {
                    pageNumber: String(this.pendingApplicationFormsPage()),
                  },
                });
              }
              this.applicationFormPage = val.params.pageNumber;
            } else {
              this.applicationFormPage = '';
            }
            this.stepPoint = step.step;
          } else {
            this.stepperHidden = true;
            this.headerLogoHidden = true;
          }
        }
        this.agencyNameValue = this.agencyName();
        this.agencyPhoneNumberValue = this.agencyPhoneNumber();
      }

      this.auth = true;
    },
  },
};
</script>

<style lang="scss">
@import '@/styles/index.scss';
</style>
